import React, { useEffect, useState } from "react";
import { apiFetch } from "./api/client";

function SecurityAlertsPanel({ deviceUuid }) {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  const fetchAlerts = async () => {
    try {
      setError("");

      const summaryResponse = await apiFetch(`/security-alerts/summary`);
      if (!summaryResponse.ok) {
        throw new Error("Failed to load alert summary");
      }

      const alertsResponse = await apiFetch(`/security-alerts?limit=20`);
      if (!alertsResponse.ok) {
        throw new Error("Failed to load security alerts");
      }

      const summaryData = await summaryResponse.json();
      const alertsData = await alertsResponse.json();

      const endpointAlerts = Array.isArray(alertsData)
        ? alertsData.filter(
            (alert) => !deviceUuid || alert.device_uuid === deviceUuid
          )
        : [];

      const now = Date.now();
      const last24Hours = endpointAlerts.filter((alert) => {
        const value = alert.created_at || alert.last_seen || alert.detected_at;
        const timestamp = value ? new Date(value).getTime() : 0;
        return timestamp > 0 && now - timestamp <= 24 * 60 * 60 * 1000;
      });

      setSummary({
        ...(summaryData || {}),
        total_alerts: endpointAlerts.length,
        open_alerts: endpointAlerts.filter(
          (alert) => !["resolved", "closed"].includes(
            String(alert.status || "open").toLowerCase()
          )
        ).length,
        high_alerts: endpointAlerts.filter(
          (alert) => String(alert.severity || "").toLowerCase() === "high"
        ).length,
        medium_alerts: endpointAlerts.filter(
          (alert) => String(alert.severity || "").toLowerCase() === "medium"
        ).length,
        new_alerts_24h: last24Hours.length,
      });
      setAlerts(endpointAlerts);
    } catch (err) {
      setError(err.message || "Unable to load security alerts");
    } finally {
      setLoading(false);
    }
  };

  const analyzeNow = async () => {
    try {
      setWorking(true);
      setError("");

      const response = await apiFetch(`/security-alerts/analyze`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Alert analysis failed");
      }

      await fetchAlerts();
    } catch (err) {
      setError(err.message || "Unable to analyze alerts");
    } finally {
      setWorking(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      setWorking(true);
      setError("");

      const response = await apiFetch(
        `/security-alerts/${alertId}/resolve`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to resolve alert");
      }

      await fetchAlerts();
    } catch (err) {
      setError(err.message || "Unable to resolve alert");
    } finally {
      setWorking(false);
    }
  };

  useEffect(() => {
    fetchAlerts();

    const interval = setInterval(() => {
      fetchAlerts();
    }, 30000);

    return () => clearInterval(interval);
  }, [deviceUuid]);

  if (loading) {
    return (
      <section style={styles.panel}>
        <h2 style={styles.title}>Security Alerts</h2>
        <p style={styles.muted}>Loading security alerts...</p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <div style={styles.pill}>Congo Market AI • Alert Engine</div>
          <h2 style={styles.title}>Security Alerts</h2>
          <p style={styles.subtitle}>
            Automated findings generated from network activity, firewall
            attempts, exposed services, and failed login behavior.
          </p>
        </div>

        <button style={styles.analyzeButton} onClick={analyzeNow} disabled={working}>
          {working ? "Working..." : "Analyze Now"}
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={styles.metricGrid}>
        <AlertMetric
          label="Open Alerts"
          value={summary?.open_alerts || 0}
          tone="blue"
        />

        <AlertMetric
          label="High Alerts"
          value={summary?.high_alerts || 0}
          tone="red"
        />

        <AlertMetric
          label="Medium Alerts"
          value={summary?.medium_alerts || 0}
          tone="amber"
        />

        <AlertMetric
          label="New 24h"
          value={summary?.new_alerts_24h || 0}
          tone="green"
        />
      </div>

      {alerts.length === 0 ? (
        <div style={styles.emptyState}>
          No open security alerts. The alert engine is watching for new findings.
        </div>
      ) : (
        <div style={styles.alertList}>
          {alerts.map((alert) => (
            <div key={alert.id} style={styles.alertCard}>
              <div style={styles.alertTop}>
                <span style={getSeverityStyle(alert.severity)}>
                  {alert.severity}
                </span>

                <span style={styles.alertType}>{alert.alert_type}</span>
              </div>

              <h3 style={styles.alertTitle}>{alert.title}</h3>

              <p style={styles.alertMessage}>{alert.message}</p>

              <div style={styles.alertMeta}>
                <span>Host: {alert.hostname || "N/A"}</span>
                <span>Source: {alert.source_ip || "N/A"}</span>
                <span>Port: {alert.target_port || "N/A"}</span>
                <span>Count: {alert.event_count || 1}</span>
              </div>

              <div style={styles.alertFooter}>
                <span style={styles.timeText}>
                  Last seen: {formatDate(alert.last_seen)}
                </span>

                <button
                  style={styles.resolveButton}
                  onClick={() => resolveAlert(alert.id)}
                  disabled={working}
                >
                  Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AlertMetric({ label, value, tone }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricTop}>
        <span style={styles.metricLabel}>{label}</span>
        <span style={{ ...styles.dot, ...getDotStyle(tone) }}></span>
      </div>

      <h3 style={styles.metricValue}>{value}</h3>
    </div>
  );
}

function getSeverityStyle(severity) {
  if (severity === "High") {
    return {
      ...styles.severityBadge,
      background: "#fee2e2",
      color: "#991b1b",
    };
  }

  if (severity === "Medium") {
    return {
      ...styles.severityBadge,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    ...styles.severityBadge,
    background: "#dcfce7",
    color: "#166534",
  };
}

function getDotStyle(tone) {
  if (tone === "red") return { background: "#ef4444" };
  if (tone === "amber") return { background: "#f59e0b" };
  if (tone === "green") return { background: "#22c55e" };
  return { background: "#2563eb" };
}

function formatDate(value) {
  if (!value) return "N/A";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    padding: "22px",
    marginBottom: "18px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "18px",
    alignItems: "flex-start",
    marginBottom: "18px",
  },
  pill: {
    display: "inline-flex",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "10px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    letterSpacing: "-0.04em",
    color: "#0f172a",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    maxWidth: "760px",
  },
  muted: {
    color: "#64748b",
  },
  analyzeButton: {
    border: "0",
    background: "#0f172a",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "14px",
    fontWeight: "900",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "14px",
    padding: "12px 14px",
    marginBottom: "16px",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
    marginBottom: "18px",
  },
  metricCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "16px",
  },
  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "900",
  },
  metricValue: {
    margin: "10px 0 0",
    fontSize: "32px",
    letterSpacing: "-0.06em",
    color: "#0f172a",
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
  },
  alertList: {
    display: "grid",
    gap: "14px",
  },
  alertCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    padding: "16px",
    background: "#f8fafc",
  },
  alertTop: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginBottom: "10px",
  },
  severityBadge: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "900",
  },
  alertType: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "800",
  },
  alertTitle: {
    margin: "0 0 6px",
    color: "#0f172a",
    fontSize: "18px",
  },
  alertMessage: {
    margin: "0 0 12px",
    color: "#475569",
    lineHeight: 1.55,
  },
  alertMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "14px",
  },
  alertFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
  },
  timeText: {
    color: "#64748b",
    fontSize: "13px",
  },
  resolveButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "9px 12px",
    borderRadius: "12px",
    fontWeight: "800",
    cursor: "pointer",
  },
  emptyState: {
    padding: "18px",
    borderRadius: "18px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    textAlign: "center",
  },
};

export default SecurityAlertsPanel;